import identity from 'lodash/identity';
import isPlainObject from 'lodash/isPlainObject';
import noop from 'lodash/noop';
import pick from 'lodash/pick';
import omit from 'lodash/omit';
import { schema } from 'normalizr';

import uuid from 'utils/id';

const idToString = (id) => (id != null ? `${id}` : id);
const defaultGenerateId = uuid;
const defaultProcessId = idToString;

/**
 * Array Schema
 * ============
 *
 * Presented as-is
 */
export class ArraySchema extends schema.Array {}

/**
 * Entity Schema
 * =============
 *
 * Modified from the default normalzr entity to allow ID generation and processing. By default, it
 * does NOT generate IDs, but casts IDs to strings if they are defined.
 *
 * To automatically generate IDs, set `generateId` to `true` or pass an ID generator function.
 * To  generate IDs, set `generateId` to `true` or pass an ID generator function.
 */
export class EntitySchema extends schema.Entity {
    /**
     * [constructor description]
     * @param  {string} key - entity key
     * @param  {Object} [definition] - definition of nested entities
     * @param  {Object} [options] - entity options
     * @param  {boolean|function} [options.generateId] - enables or disables ID generation when no
     *         ID is defined or specifies a custom ID generator. Defaults to disabled.
     * @param  {boolean|function} [options.processId] - enabled or disables casting ID processing or
     *         specifies a custom ID processor. Defaults to casting set IDs to strings.
     * @param  {function|array|string} [options.unprocessStrategy] - defines behavior for filtering
     *         an entity while denormalizing
     */
    constructor(key, definition = {}, options = {}) {
        super(key, definition, options);
        let { generateId = false, processId = true, unprocessStrategy = false } = options;

        if (generateId && typeof generateId !== 'function') {
            generateId = defaultGenerateId;
        }
        if (processId && typeof processId !== 'function') {
            processId = defaultProcessId;
        }
        if (unprocessStrategy && Array.isArray(unprocessStrategy) && unprocessStrategy.length) {
            unprocessStrategy = (
                (paths) => (entity) =>
                    pick(entity, paths)
            )(unprocessStrategy);
        }

        this._generateId = typeof generateId === 'function' ? generateId : noop;
        this._processId = typeof processId === 'function' ? processId : identity;
        this._unprocessStrategy = typeof unprocessStrategy === 'function' ? unprocessStrategy : identity;
    }

    generateId(input, parent, key) {
        return this._generateId(input, parent, key);
    }

    getId(input, parent, key) {
        const id = super.getId(input, parent, key);
        if (id == null) {
            const generatedId = this.generateId(input, parent, key);
            if (generatedId != null) {
                return generatedId;
            }
        }
        return id;
    }

    getIdAttribute(input, parent, key) {
        return typeof this.idAttribute === 'function' ? this.idAttribute(input, parent, key) : this.idAttribute;
    }

    processId(id) {
        return this._processId(id);
    }

    normalize(input, parent, key, visit, addEntity) {
        const id = this.processId(this.getId(input, parent, key));
        const idAttribute = this.getIdAttribute(input, parent, key);
        const processedInput =
            id == null
                ? input
                : {
                      ...input,
                      [idAttribute]: id,
                  };
        return super.normalize(processedInput, parent, key, visit, addEntity);
    }

    denormalize(entity, unvisit) {
        const unprocessedEntity = this._unprocessStrategy(entity);
        return super.denormalize(unprocessedEntity, unvisit);
    }
}

/**
 * Object Schema
 * =============
 *
 * Presented as-is
 */
export class ObjectSchema extends schema.Object {}

/**
 * Union Schema
 * ============
 *
 * Modified so that we can map schema keys to entity keys and vise-versa
 */
export class UnionSchema extends schema.Union {
    constructor(definition, schemaAttribute) {
        super(definition, schemaAttribute);
        this._generateEntities();
    }

    define(definition) {
        this._entities = null;
        super.define(definition);
    }

    _generateEntities() {
        this._entities = {};
        Object.keys(this.schema).forEach((schemaKey) => {
            const { key: entitiesKey } = this.schema[schemaKey];
            this._entities[entitiesKey] = schemaKey;
        });
        return this._entities;
    }

    get entities() {
        return this._entities || this._generateEntities();
    }

    getEntitiesKey = (schemaKey) => this.schema[schemaKey] && this.schema[schemaKey].key;
    getSchemaKey = (entitiesKey) => this.entities[entitiesKey];
}

/**
 * Values Schema
 * =============
 *
 * Presented as-is
 */
export class ValuesSchema extends schema.Values {}

/**
 * Pseudo-Entity Array Schema
 * ==========================
 *
 * Converts a plain array to a localized entity store, assigning IDs to each object and maintaining
 * a list of the entity IDs
 *
 * This is most useful in cases where you have array data inside an entity and you want to add,
 * remove, or update some part of it. Instead of synthesized IDs and either doing weird array
 * manipulation or creating a junk drawer of subentities from across entities that share the same ID
 * space (which can be risky), this creates a localized version of the entity/ID paradigm.
 */
export class PseudoEntityArraySchema extends ObjectSchema {
    /**
     * @param {Object} definition
     * @param {Object} params
     * @param {string} [params.idAttribute] - id attribute used by this entity.
     * @param {bool} [params.generateId] - generate a unique id if id is not set.
     * @param {bool} [params.includeIdSerialized] - include generated id key in serialized data.
     * @param {bool} [params.includeIdNormalized] - include generated id key in normalized data.
     */
    constructor(
        definition = {},
        {
            idAttribute = 'id',
            generateId = true,
            includeIdSerialized = false,
            includeIdNormalized = false,
            ...restOptions
        } = {},
    ) {
        super(definition);

        // Create an entity schema to grab ID generators from
        const options = {
            idAttribute,
            generateId,
            ...restOptions,
        };
        const pseudoEntitySchema = new EntitySchema('__pseudo__', {}, options);

        // Gets or generates an entity ID
        this._getId = (input, parent, key) =>
            pseudoEntitySchema.processId(
                isPlainObject(input)
                    ? pseudoEntitySchema.getId(input, parent, key)
                    : pseudoEntitySchema.generateId(input, parent, key),
            );

        // Adds the ID to an entity when denormalizing
        this._denormalizeIncludeId = !includeIdSerialized
            ? // if no ID should be included, we remove it from the entity descriptor.
              (entity) => omit(entity, [pseudoEntitySchema.getIdAttribute(entity)])
            : // If it should be, we make sure to put it.
              (entity, id) => ({
                  ...entity,
                  [pseudoEntitySchema.getIdAttribute(entity)]: id,
              });

        this._addId = (entity, id) => {
            if (!includeIdNormalized || !isPlainObject(entity)) {
                return entity;
            } else {
                return {
                    [pseudoEntitySchema.getIdAttribute(entity)]: id,
                    ...entity,
                };
            }
        };
    }

    getId(input, parent, key) {
        return this._getId(input, parent, key);
    }

    normalize(input, parent, key, visit, addEntity) {
        const entities = {};
        const result = [];

        (Array.isArray(input) ? input : Object.keys(input).map((k) => input[k])).forEach((value) => {
            let normalizedValue = isPlainObject(value) ? super.normalize(value, parent, key, visit, addEntity) : value;
            if (normalizedValue != null) {
                const id = this.getId(normalizedValue);

                // Ensure normalized objects get the key id.
                normalizedValue = this._addId(normalizedValue, id);

                entities[id] = normalizedValue;
                result.push(id);
            }
        });

        return { entities, result };
    }

    denormalize({ result, entities }, ...args) {
        return result.map((key) =>
            isPlainObject(entities[key])
                ? this._denormalizeIncludeId(super.denormalize(entities[key], ...args), key)
                : entities[key],
        );
    }
}
